/**
 * assets/bulk-qty-selector.js
 * -----------------------------------------------------------------------------
 * Bulk Quantity Selector v2 — behaviour for <bulk-qty-selector> rendered by
 * snippets/bulk-qty-selector.liquid, plus the custom-quantity box that lives
 * inside the product form (#Quantity-Form-{section}).
 *
 * WHAT IT DOES
 *  - Resolves the active tier from whatever quantity is in the box:
 *      active = highest tier whose min <= qty        (8 pcs -> "5 pcs" card)
 *      total  = qty * active.price                   (never the card's own total)
 *  - Keeps the qualifying card lit and adds a "Custom · 8 pcs" badge when the
 *    quantity is not exactly a tier minimum.
 *  - Nudge banner (neutral tint, never red), three states:
 *      next tier   -> "Add 2 more to get ₹116/pc (Save 4%)"   + "Add 2" pill
 *      last tier   -> "Add 7 more to get our lowest price: ₹114/pc"
 *      best price  -> "You have the lowest price per piece"   (green)
 *    Lowering the quantity just re-renders; there is no penalty message.
 *  - Custom box: helper hint above the input, +10/+25/+50 chips beside it,
 *    live "8 pcs × ₹115 = ₹920 · You save ₹48" line below it, accent border
 *    while the value is custom.
 *  - Add-to-cart label carries the live total ("ADD TO CART · ₹920").
 *
 * HOW IT PLAYS WITH THE EXISTING SCRIPTS
 *  - quantity-guard.js overrides the `value` setter on `.custom-quantity-input`
 *    while a cart sync is pending and silently drops writes. We write through
 *    the native setter so tier taps / chips are never dropped, then dispatch
 *    `input` + `change` so Dawn, quantity-guard and price-per-item all see it.
 *  - quantity-guard.js clamps to DEFAULT_MAX_QTY (99) unless the input has a
 *    max. Raise that constant (or pass data-max from inventory) if wholesale
 *    buyers should go past 99 — see the note in the change list.
 *  - Custom element: connectedCallback re-runs if Dawn re-renders the section
 *    on variant change, so no manual re-init is needed.
 *
 * Loaded from snippets/buy-buttons.liquid.
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (customElements.get('bulk-qty-selector')) return;

  var nativeSet = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;

  function inr(n) {
    n = Number(n) || 0;
    var whole = Math.abs(n - Math.round(n)) < 0.005;
    return '₹' + (whole ? Math.round(n).toLocaleString('en-IN') : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }
  function pcs(q) {
    return q + (q === 1 ? ' pc' : ' pcs');
  }

  class BulkQtySelector extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.base = parseFloat(this.dataset.base) || 0;
      this.mrp = parseFloat(this.dataset.mrp) || this.base;
      this.moq = parseInt(this.dataset.moq, 10) || 1;

      var raw = [];
      try { raw = JSON.parse(this.dataset.tiers || '[]'); } catch (e) { raw = []; }
      this.tiers = [{ min: this.moq, price: this.base }]
        .concat(raw.map(function (t) { return { min: parseInt(t.min, 10), price: parseFloat(t.price) }; }))
        .filter(function (t) { return t.min > 0 && t.price > 0; })
        .sort(function (a, b) { return a.min - b.min; });
      this.last = this.tiers[this.tiers.length - 1];

      this.input = document.getElementById(this.dataset.input);
      if (!this.input) return;

      this.formBox = document.getElementById(this.dataset.form);
      this.cta = document.getElementById(this.dataset.cta);
      this.cards = Array.prototype.slice.call(this.querySelectorAll('.bqs__tier'));
      this.nudge = this.querySelector('[data-bqs-nudge]');
      this.nudgeText = this.querySelector('[data-bqs-nudge-text]');
      this.nudgeBtn = this.querySelector('[data-bqs-nudge-btn]');

      if (this.formBox) {
        this.formBox.classList.add('bqs-custom');
        // Break the quantity box out of the "quantity | buttons" flex row so it
        // gets the full width; the buttons drop to their own row underneath.
        var row = this.formBox.closest('.quantity-buy-btn-custom');
        if (row) row.classList.add('bqs-active');
        var btns = this.formBox.closest('.product-form__buttons');
        if (btns) btns.classList.add('bqs-active');
        this.hint = this.formBox.querySelector('[data-bqs-hint]');
        this.chips = this.formBox.querySelector('[data-bqs-chips]');
        this.live = this.formBox.querySelector('[data-bqs-live]');
        if (this.hint) this.hint.hidden = false;
        if (this.chips) this.chips.hidden = false;
        if (this.live) this.live.hidden = false;
      }

      // --- events -----------------------------------------------------------
      this.cards.forEach(function (card) {
        card.addEventListener('click', function () {
          self.setQty(parseInt(card.dataset.min, 10));
          self.track('bulk_tier_tap', { tier: card.dataset.min });
        });
      });
      if (this.chips) {
        this.chips.addEventListener('click', function (e) {
          var chip = e.target.closest('[data-bqs-add]');
          if (!chip) return;
          var add = parseInt(chip.dataset.bqsAdd, 10) || 0;
          self.setQty(self.readQty() + add);
          self.track('bulk_chip_tap', { add: add });
        });
      }
      if (this.nudgeBtn) {
        this.nudgeBtn.addEventListener('click', function () {
          var from = self.readQty();
          var next = self.nextTier(from);
          if (!next) return;
          self.setQty(next.min);
          self.track('bulk_nudge_tap', { from: from, to: next.min });
        });
      }
      this._onInput = function () { self.render(); };
      this._onChange = function () { self.render(); self.track('bulk_custom_qty', { qty: self.readQty() }); };
      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('change', this._onChange);

      this.render();
    }

    disconnectedCallback() {
      if (!this.input) return;
      this.input.removeEventListener('input', this._onInput);
      this.input.removeEventListener('change', this._onChange);
    }

    // --- helpers --------------------------------------------------------------
    readQty() {
      var v = parseInt(this.input.value, 10);
      return isNaN(v) || v < this.moq ? this.moq : v;
    }

    setQty(n) {
      n = Math.max(this.moq, n | 0);
      var max = parseInt(this.input.getAttribute('max') || this.input.dataset.max, 10);
      if (!isNaN(max) && max > 0 && n > max) n = max;
      // Native setter: bypasses quantity-guard's stale-write protection.
      nativeSet.call(this.input, n);
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.render();
    }

    activeTier(qty) {
      var t = this.tiers[0];
      for (var i = 0; i < this.tiers.length; i++) if (qty >= this.tiers[i].min) t = this.tiers[i];
      return t;
    }

    nextTier(qty) {
      for (var i = 0; i < this.tiers.length; i++) if (this.tiers[i].min > qty) return this.tiers[i];
      return null;
    }

    track(name, data) {
      try {
        if (window.dataLayer) window.dataLayer.push(Object.assign({ event: name }, data));
        document.dispatchEvent(new CustomEvent(name, { detail: data }));
      } catch (e) { /* analytics is best-effort */ }
    }

    // --- render ---------------------------------------------------------------
    render() {
      var qty = this.readQty();
      var active = this.activeTier(qty);
      var next = this.nextTier(qty);
      var total = qty * active.price;
      var isCustom = !this.tiers.some(function (t) { return t.min === qty; });
      var saveVsBase = qty * (this.base - active.price);

      // tier cards
      this.cards.forEach(function (card) {
        var on = parseInt(card.dataset.min, 10) === active.min;
        card.classList.toggle('is-active', on);
        card.setAttribute('aria-pressed', on ? 'true' : 'false');
        var badge = card.querySelector('.bqs__badge--custom');
        if (badge) {
          badge.hidden = !(on && isCustom);
          badge.textContent = 'Custom · ' + pcs(qty);
        }
      });

      // nudge banner
      if (this.nudge) {
        this.nudge.classList.toggle('is-best', !next);
        if (!next) {
          this.nudgeText.textContent = 'You have the lowest price per piece' + (isCustom ? ' · ' + pcs(qty) + ' at ' + inr(active.price) : '');
          this.nudgeBtn.hidden = true;
        } else {
          var gap = next.min - qty;
          if (next === this.last) {
            this.nudgeText.textContent = 'Add ' + gap + ' more to get our lowest price: ' + inr(next.price) + '/pc';
          } else {
            var pct = Math.round((active.price - next.price) / active.price * 100);
            this.nudgeText.textContent = 'Add ' + gap + ' more to get ' + inr(next.price) + '/pc (Save ' + pct + '%)';
          }
          this.nudgeBtn.hidden = false;
          this.nudgeBtn.textContent = 'Add ' + gap;
        }
      }

      // custom box
      if (this.formBox) {
        this.formBox.classList.toggle('is-custom', isCustom);
        if (this.hint) {
          this.hint.innerHTML = next
            ? 'Tap to enter custom qty <b>(' + this.last.min + '+ unlocks ' + inr(this.last.price) + '/pc rate)</b>'
            : 'Enter any quantity, <b>' + inr(this.last.price) + '/pc applies to all</b>';
        }
        if (this.live) {
          this.live.innerHTML = '<b>' + pcs(qty) + ' × ' + inr(active.price) + ' = ' + inr(total) + '</b>' +
            (saveVsBase > 0.005 ? ' · You save ' + inr(saveVsBase) + ' vs single price' : ' · Regular price');
        }
      }

      // add-to-cart label
      if (this.cta) {
        var slot = this.cta.querySelector('.bqs__cta-total');
        if (!slot) {
          slot = document.createElement('span');
          slot.className = 'bqs__cta-total';
          this.cta.appendChild(slot);
        }
        slot.textContent = '· ' + inr(total);
      }

      this.dispatchEvent(new CustomEvent('bqs:change', { bubbles: true, detail: { qty: qty, unit: active.price, total: total, tier: active.min } }));
    }
  }

  customElements.define('bulk-qty-selector', BulkQtySelector);
})();
